import type { IntelligenceNavigationIntent } from './types';

type Surface = IntelligenceNavigationIntent['target']['surface'];
type Kind = IntelligenceNavigationIntent['kind'];

interface NavigationRule {
  sectionId: string;
  controlIds?: readonly string[];
  resourceRequired?: boolean;
}

const commonClientRules: Partial<Record<Kind, NavigationRule>> = {
  OPEN_OVERVIEW: { sectionId: 'overview' },
  OPEN_EVIDENCE: { sectionId: 'evidence', controlIds: ['evidence-upload', 'evidence-sources'] },
  OPEN_EVIDENCE_REVIEW: { sectionId: 'evidence', controlIds: ['evidence-review'] },
  OPEN_RETURNS: { sectionId: 'returns' },
  OPEN_RETURN_FACTS: { sectionId: 'returns', controlIds: ['return-facts'] },
  VIEW_CALCULATION_TRACE: { sectionId: 'returns', controlIds: ['calculation-trace'] },
  CONTINUE_TO_FILING_REVIEW: { sectionId: 'returns', controlIds: ['filing-review'] },
  OPEN_FILING: { sectionId: 'filing' },
};

export const intelligenceEvidenceSourcesEvent = 'amoye:open-evidence-sources';

const rules: Record<Surface, Partial<Record<Kind, NavigationRule>>> = {
  BUSINESS: { ...commonClientRules, OPEN_INVOICES: { sectionId: 'invoices' } },
  PERSONAL: commonClientRules,
  PRACTICE: {
    ...commonClientRules,
    OPEN_INVOICES: { sectionId: 'invoices' },
    OPEN_CLIENT: { sectionId: 'overview', resourceRequired: true },
  },
};

/**
 * Converts a server response into an app-owned, allowlisted navigation target.
 * The assistant never supplies a URL, selector, route name or executable action.
 */
export function resolveIntelligenceNavigation(intent: IntelligenceNavigationIntent,
  expectedSurface: Surface): IntelligenceNavigationIntent['target'] | undefined {
  if (intent.effect !== 'NAVIGATE_FOCUS_ONLY' || intent.requiresReauthorization !== true
    || intent.target.surface !== expectedSurface) return undefined;
  const rule = rules[expectedSurface][intent.kind];
  if (!rule || intent.target.sectionId !== rule.sectionId) return undefined;
  if (rule.resourceRequired && !intent.target.resourceRef) return undefined;
  if (rule.controlIds && (!intent.target.controlId || !rule.controlIds.includes(intent.target.controlId))) return undefined;
  if (!rule.controlIds && intent.target.controlId) return undefined;
  return { surface: expectedSurface, sectionId: rule.sectionId,
    ...(intent.target.controlId ? { controlId: intent.target.controlId } : {}),
    ...(intent.target.resourceRef ? { resourceRef: intent.target.resourceRef } : {}) };
}

/** App-owned forms opt into this guard with data-amoye-unsaved="true". */
export function confirmIntelligenceNavigationAway(confirmLeave: (message: string) => boolean = (message) =>
  window.confirm(message)): boolean {
  if (typeof document === 'undefined' || !document.querySelector('form[data-amoye-unsaved="true"]')) return true;
  return confirmLeave('You have unsaved changes in this screen. Leave it and discard those changes?');
}

/** Reveals only an already allowlisted, app-owned control. This helper owns
 * the DOM behavior; assistant output never supplies a selector or URL. */
export function focusIntelligenceNavigationControl(controlId?: string): void {
  if (!controlId || typeof window === 'undefined' || typeof document === 'undefined') return;
  window.setTimeout(() => {
    if (controlId === 'evidence-sources') window.dispatchEvent(new Event(intelligenceEvidenceSourcesEvent));
    window.setTimeout(() => {
      const control = document.getElementById(controlId);
      if (control instanceof HTMLDetailsElement) control.open = true;
      control?.focus();
      control?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 0);
  }, 0);
}
